import type { TaxPolicy, TaxResult, YearTaxInput } from '../policy';
import {
  LATEST_THAI_TAX_YEAR,
  RMF_ELIGIBILITY_AGE,
  resolveThaiTaxConfig,
  type ThaiTaxYearConfig,
} from './config';
import {
  applyCombinedCap,
  dividendTaxCredit,
  employmentExpenseDeduction,
  fundDeduction,
  marginalRate,
  pitOnNetIncome,
} from './pit';

/**
 * ThailandTaxPolicy (PLAN.md §2d "Taxes (pluggable)", CLAUDE.md "Tax logic stays behind
 * TaxPolicy"). M4 implementation: progressive PIT, allowances + the employment expense deduction,
 * SET-listed capital-gains EXEMPTION, dividend WHT (final-tax election AND the s.47 bis
 * imputation/credit election — whichever is cheaper), interest WHT, the RMF/SSF/Thai-ESG/PVD
 * deductions with the THB 500k combined cap, the Por.161/162 foreign-remittance rule, and the
 * RMF/SSF early-withdrawal CLAWBACK + surcharge.
 *
 * EVERY figure comes from the year-versioned ./config (named constants + RD/Big-4/Sherrings
 * citations); this file holds only the MECHANICS that combine them. Behaviour is pinned by the
 * golden tests in test/tax-th.test.ts against published Revenue Department tables.
 *
 * ============================ HONEST BOUNDARIES (PLAN.md) ============================
 * Thai tax law is evolving and several edges are deliberately NOT modelled. These are explicit
 * TODOs, never silent gaps:
 *  - TODO(out-of-scope): DTA foreign-tax credits — foreign tax paid is NOT credited against the
 *      Thai liability on remitted foreign income (we tax the gross remittance). A double-tax-treaty
 *      credit would reduce it; modelling it needs per-country treaty data.
 *  - TODO(out-of-scope): BOI privileges and LTR-visa exemptions (Royal Decree 743 exempts certain
 *      LTR holders' remitted foreign income) — these would zero parts of the foreign-remittance tax.
 *  - TODO(out-of-scope): business-income nuances (s.40(5)–(8) actual-vs-standard expense regimes);
 *      we treat `assessableIncome` as employment income plus pass-through investment income only.
 *  - TODO(not-yet-law): the WORLDWIDE-income proposal (tax foreign income when EARNED, not when
 *      remitted) is a draft, NOT enacted as of 2026-06-14. We model only the enacted Por.161/162
 *      remittance basis. Add a config flag + new tax-year entry if/when it passes.
 *  - TODO(no-RMD): Thailand has NO US-style Required Minimum Distributions. We model NOTHING here
 *      on purpose — there is no forced-withdrawal tax event — but state it so the US module's RMDs
 *      (M8) stay isolated to that jurisdiction.
 *  - TODO(refine): the child-allowance "+THB 30k for the 2nd child born >=2018" bump, parental-care
 *      and disability allowances, donation deductions, and the >65/disabled income exemption are
 *      not yet modelled (only personal + spouse + flat child allowance).
 *  - TODO(refine): RMF's "held >= 5 years AND to age 55" twin condition and per-fund holding-period
 *      tracking — the clawback here keys off the account KIND + the household age-55 gate, not
 *      per-lot purchase dates (the engine does not yet carry lot ages). SSF's 10-year and Thai
 *      ESG's 5/8-year per-lot tracking is likewise approximate.
 *  - TODO(refine): the LTF->Thai ESGX switch tranche (up to THB 500k over 2025–2029) and the
 *      post-2026 Thai ESG reversion to THB 100k / 8-year hold (see config conflict notes).
 *  - TODO(refine): dividends from a payer taxed at a NON-standard CIT rate (BOI/SME) change the
 *      s.47 bis gross-up rate; we use the standard 20% corporate rate for the credit.
 * ====================================================================================
 */
export class ThailandTaxPolicy implements TaxPolicy {
  readonly jurisdiction = 'TH';

  /**
   * Whether to use the s.47 bis dividend tax-CREDIT (imputation/gross-up) election instead of the
   * 10% final WHT. Default `'auto'` picks the cheaper of the two per year (the rational filer's
   * choice). `'final'` / `'credit'` force one election (for what-if analysis and golden tests).
   */
  readonly dividendElection: 'auto' | 'final' | 'credit';

  /** Pin a specific tax year's config; default tracks the latest confirmed year. */
  readonly forcedTaxYear: number | undefined;

  constructor(
    options: {
      readonly dividendElection?: 'auto' | 'final' | 'credit';
      readonly taxYear?: number;
    } = {},
  ) {
    this.dividendElection = options.dividendElection ?? 'auto';
    this.forcedTaxYear = options.taxYear;
  }

  compute(input: YearTaxInput): TaxResult {
    const cfg = resolveThaiTaxConfig(this.forcedTaxYear ?? input.year);

    // ---- Deductions from contributions to tax-advantaged vehicles -------------------------
    // Base income for the percentage caps: assessable income (employment + pass-through).
    const incomeForCaps = Math.max(0, input.assessableIncome);
    const contrib = input.contributionsByKind ?? {};
    const rmfDed = fundDeduction(contrib.rmf ?? 0, incomeForCaps, cfg.rmf);
    const ssfDed = fundDeduction(contrib.ssf ?? 0, incomeForCaps, cfg.ssf);
    const esgDed = fundDeduction(
      (contrib.thai_esg ?? 0) + (contrib.thai_esgx ?? 0),
      incomeForCaps,
      cfg.thaiEsg,
    );
    const pvdDed = fundDeduction(contrib.pvd ?? 0, incomeForCaps, cfg.pvd);
    const investmentDeductions = applyCombinedCap(
      [
        { deduction: rmfDed, inCombinedCap: cfg.rmf.inCombinedCap },
        { deduction: ssfDed, inCombinedCap: cfg.ssf.inCombinedCap },
        { deduction: pvdDed, inCombinedCap: cfg.pvd.inCombinedCap },
        { deduction: esgDed, inCombinedCap: cfg.thaiEsg.inCombinedCap },
      ],
      cfg.combinedRetirementCap,
    );

    // ---- Allowances + the employment expense deduction ------------------------------------
    const employmentIncome = input.employmentIncome ?? input.assessableIncome;
    const expenseDeduction = employmentExpenseDeduction(Math.max(0, employmentIncome), cfg);
    const allowances = personalAllowances(input, cfg);

    // ---- Build the PIT base ---------------------------------------------------------------
    // Foreign-sourced income REMITTED into Thailand (Por.161/162) is assessable for residents.
    // We add it to the PIT base (gross — no DTA credit; see TODO above).
    const foreignRemitted = Math.max(0, input.foreignIncomeRemitted);

    // SET-listed capital gains sold on the exchange are EXEMPT for individuals — we never add
    // `input.capitalGains.setListed` to the base. Foreign-sleeve gains are taxed only when they
    // arrive as remitted foreign income (folded into `foreignIncomeRemitted` by the caller), so we
    // do not double-count `input.capitalGains.foreign` here.
    const baseBeforeDividends =
      Math.max(0, input.assessableIncome) +
      foreignRemitted -
      expenseDeduction -
      allowances -
      investmentDeductions;

    // ---- Dividends: pick the cheaper election (final WHT vs s.47 bis credit) --------------
    const dividends = Math.max(0, input.dividends);
    const interest = Math.max(0, input.interest ?? 0);

    // Election A: 10% final WHT — dividends excluded from the PIT base, WHT is the final tax.
    const netA = Math.max(0, baseBeforeDividends);
    const pitA = pitOnNetIncome(netA, cfg.pitBrackets);
    const dividendTaxFinal = dividends * cfg.dividendWhtRate;
    const totalA = pitA + dividendTaxFinal;

    // Election B: s.47 bis credit — gross dividends up by the corporate tax already paid, add the
    // grossed-up income (`dividends + credit`) to the PIT base, then CREDIT that gross-up against
    // the PIT due. The dividend's PERSONAL tax cost is the extra PIT the grossed-up income adds,
    // LESS the imputation credit: `(pitB - pitA) - credit`. (The 10% WHT is a PREPAYMENT settled
    // inside that liability, NOT a further reduction — subtracting it again would double-count.)
    // Floored at 0: when the credit exceeds the extra PIT, the RD issues a refund, which the engine
    // does not model as negative tax — see the class TODO; the election simply costs nothing here.
    const credit = dividendTaxCredit(dividends, cfg.corporateTaxRate);
    const netB = Math.max(0, baseBeforeDividends + dividends + credit);
    const pitB = pitOnNetIncome(netB, cfg.pitBrackets);
    const dividendTaxCreditNet = Math.max(0, pitB - pitA - credit);
    const totalB = pitA + dividendTaxCreditNet;

    const useCredit =
      this.dividendElection === 'credit' || (this.dividendElection === 'auto' && totalB < totalA);
    const pitOnEmploymentEtc = pitA; // base PIT (excludes dividends in both elections' attribution)
    const dividendTax = useCredit ? dividendTaxCreditNet : dividendTaxFinal;

    // ---- Interest: 15% final WHT [PWC-PIT] ------------------------------------------------
    // Modelled as a final-tax election (the common default for individuals). The
    // include-in-income-and-credit alternative is symmetric to dividends — TODO if needed.
    const interestTax = interest * cfg.interestWhtRate;

    // ---- RMF/SSF early-withdrawal clawback ------------------------------------------------
    // `netA` is the year's net taxable income; the clawback stacks the early-withdrawn amount ON
    // TOP of it so the recovered tax is at the MARGINAL rate (the deductions sat at the top of the
    // income stack), not the zero-floor base rate.
    const clawback = earlyWithdrawalClawback(input, cfg, netA);

    // ---- Assemble ------------------------------------------------------------------------
    const byCategory: Record<string, number> = {};
    if (pitOnEmploymentEtc > 0) {
      byCategory.pit = pitOnEmploymentEtc;
    }
    if (dividendTax > 0) {
      byCategory.dividend = dividendTax;
    }
    if (interestTax > 0) {
      byCategory.interest = interestTax;
    }
    if (clawback > 0) {
      byCategory.clawback = clawback;
    }
    const total = pitOnEmploymentEtc + dividendTax + interestTax + clawback;

    return {
      total,
      byCategory,
      marginalRate: marginalRate(useCredit ? netB : netA, cfg.pitBrackets),
      clawback,
    };
  }
}

/** Personal + spouse + child allowances claimed for this filing. */
function personalAllowances(input: YearTaxInput, cfg: ThaiTaxYearConfig): number {
  let total = cfg.personalAllowance;
  const filing = input.filing;
  if (filing?.claimSpouse) {
    total += cfg.spouseAllowance;
  }
  if (filing?.children && filing.children > 0) {
    total += cfg.childAllowance * filing.children;
  }
  return total;
}

/**
 * RMF/SSF early-withdrawal clawback (PLAN.md §2d). Withdrawing RMF before age 55 (the household
 * has not reached `RMF_ELIGIBILITY_AGE`), or SSF before its holding period, recovers the tax that
 * the past deductions saved PLUS a surcharge.
 *
 * MODELLING NOTE (honest approximation — see the class TODOs): the engine does not yet carry
 * per-lot purchase dates, so we cannot know the exact past deductions. We recover the deduction
 * benefit at the MARGINAL rate — the early-withdrawn amount is stacked ON TOP of the year's net
 * taxable income (`netTaxableIncome`), since the original deductions reduced income from the top of
 * the stack — then a single annualised 12-month surcharge proxy (1.5%/month) is added. This is the
 * deliberate, visible boundary; refine to true per-lot, per-year recapture when lot ages land.
 */
function earlyWithdrawalClawback(
  input: YearTaxInput,
  cfg: ThaiTaxYearConfig,
  netTaxableIncome: number,
): number {
  const ageEligible = input.ages.some((a) => a >= RMF_ELIGIBILITY_AGE);
  const rmfWithdrawal = input.withdrawalsByKind.rmf ?? 0;
  const ssfWithdrawal = input.withdrawalsByKind.ssf ?? 0;

  // RMF: early only if NO household member has reached the age-55 gate.
  const earlyRmf = ageEligible ? 0 : Math.max(0, rmfWithdrawal);
  // SSF: a withdrawal here is treated as early (the ordering strategy normally leaves SSF until
  // its hold elapses; if it is drawn, we conservatively assess the clawback). TODO: gate on
  // per-lot 10-year holding once lot ages are tracked.
  const earlySsf = Math.max(0, ssfWithdrawal);

  const earlyTotal = earlyRmf + earlySsf;
  if (earlyTotal <= 0) {
    return 0;
  }
  // Recovered tax = the marginal PIT on the withdrawn amount stacked atop the year's net income.
  const base = Math.max(0, netTaxableIncome);
  const recoveredTax =
    pitOnNetIncome(base + earlyTotal, cfg.pitBrackets) - pitOnNetIncome(base, cfg.pitBrackets);
  const surcharge = recoveredTax * cfg.earlyWithdrawalSurchargeMonthly * 12;
  return recoveredTax + surcharge;
}

/** Re-export the latest confirmed tax year so callers can label results. */
export { LATEST_THAI_TAX_YEAR };
